import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { PIPELINE_API } from "@/components/project-tabs/constants";

const API = PIPELINE_API;

/**
 * Hook for real-time artifact collaboration.
 * Manages presence, cursor broadcasting, and content sync.
 */
export const useArtifactCollaboration = (artifactId, isEditing) => {
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [remoteCursors, setRemoteCursors] = useState({});
  const [remoteContent, setRemoteContent] = useState(null);
  const eventSourceRef = useRef(null);
  const cursorThrottleRef = useRef(null);

  // Connect SSE when editing
  useEffect(() => {
    if (!artifactId || !isEditing) {
      // Just fetch users list for read-only view
      if (artifactId) {
        axios.get(`${API}/artifacts/${artifactId}/collab/users`, { withCredentials: true })
          .then(res => setRemoteUsers(res.data.users || []))
          .catch(() => {});
      }
      return;
    }

    const es = new EventSource(`${API}/artifacts/${artifactId}/collab/stream`, { withCredentials: true });

    es.onmessage = (e) => {
      try {
        const event = JSON.parse(e.data);
        switch (event.type) {
          case "init":
            setRemoteUsers(event.data.users || []);
            break;
          case "user_joined":
            setRemoteUsers(prev => {
              if (prev.find(u => u.user_id === event.data.user_id)) return prev;
              return [...prev, event.data];
            });
            break;
          case "user_left":
            setRemoteUsers(prev => prev.filter(u => u.user_id !== event.data.user_id));
            setRemoteCursors(prev => {
              const next = { ...prev };
              delete next[event.data.user_id];
              return next;
            });
            break;
          case "cursor_update":
            setRemoteCursors(prev => ({
              ...prev,
              [event.data.user_id]: event.data,
            }));
            break;
          case "content_update":
            setRemoteContent(event.data);
            break;
          case "heartbeat":
            setRemoteUsers(event.data.users || []);
            break;
          default:
            break;
        }
      } catch {}
    };

    es.onerror = () => {
      // Reconnect after delay
      setTimeout(() => {
        es.close();
      }, 3000);
    };

    eventSourceRef.current = es;

    return () => {
      es.close();
      // Notify leave
      axios.post(`${API}/artifacts/${artifactId}/collab/leave`, {}, { withCredentials: true }).catch(() => {});
    };
  }, [artifactId, isEditing]);

  // Broadcast cursor position (throttled to 200ms)
  const broadcastCursor = useCallback((cursorPos, selectionRange) => {
    if (!artifactId || !isEditing) return;
    if (cursorThrottleRef.current) return;

    cursorThrottleRef.current = setTimeout(() => {
      cursorThrottleRef.current = null;
    }, 200);

    axios.post(`${API}/artifacts/${artifactId}/collab/cursor`, {
      cursor: cursorPos,
      selection: selectionRange,
    }, { withCredentials: true }).catch(() => {});
  }, [artifactId, isEditing]);

  // Broadcast content edit
  const broadcastEdit = useCallback((content) => {
    if (!artifactId) return;
    axios.post(`${API}/artifacts/${artifactId}/collab/edit`, { content }, { withCredentials: true }).catch(() => {});
  }, [artifactId]);

  return {
    remoteUsers,
    remoteCursors,
    remoteContent,
    broadcastCursor,
    broadcastEdit,
  };
};
